import {
  applyImputationRulesAll,
  ApplyRulesInterrupted,
  mergeApplyResults,
} from "../api/imputationRules";
import type {
  ImputationRule,
  ImputationRuleApplyResult,
  ImputationRuleInput,
  RuleMatchField,
} from "../api/types";
import { localDayKey, localMidnight } from "./date";

/** Borne TAWA d'une application : `to` au plus 365 jours après `from`. */
const APPLY_SPAN_DAYS = 365;

/** Fenêtre `index` d'un an, bornes incluses, en remontant depuis aujourd'hui
 * (0 : l'année qui finit aujourd'hui). Les fenêtres se suivent sans se
 * chevaucher. */
export function applyWindow(
  today: Date,
  index: number,
): { from: string; to: string } {
  const end = localMidnight(today, -index * (APPLY_SPAN_DAYS + 1));
  return {
    from: localDayKey(localMidnight(end, -APPLY_SPAN_DAYS)),
    to: localDayKey(end),
  };
}

export interface RuleApplied {
  rule: ImputationRule;
  /** Cumul de ce qui a été imputé, null si rien n'a pu l'être. */
  applied: ImputationRuleApplyResult | null;
  /** Faux quand l'application s'est arrêtée en route. */
  complete: boolean;
}

/** Crée la règle puis applique les règles actives au temps passé, une année
 * après l'autre, jusqu'à `windows` ans ou jusqu'à une année sans activité.
 * Sans `worker_url`, tous les salariés. Une création refusée lève ; un échec
 * d'application garde la règle et rend ce qui a été écrit. */
export async function createRuleAndApply(
  create: (input: ImputationRuleInput) => Promise<ImputationRule>,
  input: ImputationRuleInput,
  windows: number,
  today = new Date(),
): Promise<RuleApplied> {
  const rule = await create(input);
  const scope = input.worker_url ? { worker_url: input.worker_url } : {};
  let applied: ImputationRuleApplyResult | null = null;
  try {
    for (let i = 0; i < windows; i++) {
      const page = await applyImputationRulesAll(
        {
          ...applyWindow(today, i),
          ...scope,
          dry_run: false,
          rule_id: rule.id,
        },
        () => {},
      );
      applied = mergeApplyResults(applied, page);
      if (page.workers_total === 0) break;
    }
  } catch (e) {
    const done = e instanceof ApplyRulesInterrupted ? e.done : null;
    return {
      rule,
      applied: done ? mergeApplyResults(applied, done) : applied,
      complete: false,
    };
  }
  return { rule, applied, complete: true };
}

/** Un remote git normalisé s'écrit « org/nom » ; un nom de dossier n'a pas de
 * barre oblique. */
export function ruleFieldOf(value: string): RuleMatchField {
  return value.includes("/") ? "repo" : "project";
}

/** Ce que le formulaire fait choisir : un libellé de code (dépôt ou dossier,
 * déduit par `ruleFieldOf`) ou un domaine de site web. */
export type RuleKind = "code" | "domain";

export function ruleKindOf(field: string | null | undefined): RuleKind {
  return field === "domain" ? "domain" : "code";
}

/** « senacle/forum », « senacle/forum (v2/*) ». */
export function ruleTitle(
  r: Pick<ImputationRule, "match_value" | "branch_pattern">,
): string {
  return r.branch_pattern
    ? `${r.match_value} (${r.branch_pattern})`
    : r.match_value;
}
